import { Head, router } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Github } from '@/components/icons';
import { Button } from '@/components/ui/button';
import type { JudgesShow } from '@/types';
import { Heading } from './components/Heading';
import { PlatanusLogo } from './components/PlatanusLogo';
import { StageList } from './components/StageList';

// /judges — signed out it is a GitHub sign-in that comes back here; signed in it is the
// judge's stages, or a "this GitHub account is not a judge" notice when the handle is
// not on the running edition's roster (JudgesController).
export default function Show({
  judge,
  stages,
  title,
  eventName,
  signedIn,
  githubHandle,
  error,
}: JudgesShow) {
  useEffect(() => {
    if (error) {
      toast.error(error.replace(/_/g, ' '));
    }
  }, [error]);

  if (judge) {
    return (
      <>
        <Head title={title} />
        <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
          <StageList judge={judge} stages={stages} eventName={eventName} />
          <div className="pb-12">
            <PlatanusLogo />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head title={title} />
      <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
        <div className="flex min-h-screen flex-col bg-background px-4 py-12">
          <div className="flex flex-1 items-center justify-center">
            <div className="flex w-full max-w-md flex-col gap-8">
              {signedIn ? (
                <NotAJudge githubHandle={githubHandle} />
              ) : (
                <SignIn />
              )}
            </div>
          </div>
          <PlatanusLogo />
        </div>
      </div>
    </>
  );
}

function SignIn() {
  const [isLoading, setIsLoading] = useState(false);

  // Same flow as /login, but the GitHub round-trip lands back on /judges.
  const handleGithubLogin = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/auth/sign-in/social', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'github',
          callbackURL: '/judges',
          errorCallbackURL: '/judges?error=unauthorized',
        }),
      });
      const body = (await response.json()) as { url?: string };

      if (!body.url) {
        throw new Error('Sign-in failed');
      }

      window.location.href = body.url;
    } catch (signInError) {
      toast.error('Error al iniciar sesión con GitHub');
      console.error(signInError);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="text-center">
        <Heading first="Portal de" second="Jueces" />
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Ingresa con la cuenta de GitHub que registraste con nosotros
        </p>
      </div>
      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <Button
          onClick={handleGithubLogin}
          disabled={isLoading}
          size="lg"
          className="w-full"
        >
          <Github className="mr-2 h-5 w-5" />
          {isLoading ? 'Conectando...' : 'Iniciar sesión con GitHub'}
        </Button>
      </div>
    </>
  );
}

function NotAJudge({ githubHandle }: { githubHandle: string | null }) {
  const [isLoading, setIsLoading] = useState(false);

  // /auth/sign_out lands on the home page; come back here to sign in again.
  const handleSignOut = () => {
    setIsLoading(true);
    router.delete('/auth/sign_out', {
      onFinish: () => router.visit('/judges'),
    });
  };

  return (
    <>
      <div className="text-center">
        <Heading first="Portal de" second="Jueces" />
      </div>
      <div className="space-y-6 border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <p className="text-center font-mono text-primary/80 text-sm">
          {githubHandle ? (
            <>
              La cuenta de GitHub{' '}
              <span className="font-bold text-primary">@{githubHandle}</span> no
              está registrada como jurado de esta edición.
            </>
          ) : (
            'Tu sesión no tiene una cuenta de GitHub asociada.'
          )}
        </p>
        <p className="text-center font-mono text-primary/60 text-xs">
          Si crees que es un error, avísale al equipo de Platanus Hack.
        </p>
        <Button
          onClick={handleSignOut}
          disabled={isLoading}
          size="lg"
          variant="outline"
          className="w-full"
        >
          <Github className="mr-2 h-5 w-5" />
          Entrar con otra cuenta
        </Button>
      </div>
    </>
  );
}
